import React, { useState, useCallback, useContext } from "react";
import QKModal, { MODAL_CLOSE_DELAY } from "./index";
import styled from "styled-components/native";
import { ScrollView } from "react-native";
import Text from "../questkit/text";
import Button from "../questkit/button";
import Switch from "../questkit/switch";
import { SnackbarContext, SnackbarSeverity } from "../snackbar/SnackbarContext";
import {
  useRequest,
  createRequest,
  noopRequest,
} from "@app/util/client/requests";
import { apiRequest } from "@app/util/client";
import Loader from "../animated/loader";
import { logOut } from "@app/screens/LogoutScreen";
import { colors } from "@app/themes/Colors";

interface DeleteAccountModalProps {
  showModal: boolean;
  setShowModal: (show: boolean) => void;
}

interface DeletionAnalysis {
  canDelete: boolean;
  blockingIssues: string[];
  personalWorkspace: {
    quests: {
      id: string;
      title: string;
      totalRuns: number;
      willBeDeleted: true;
    }[];
  };
  workspaces: {
    id: string;
    name: string;
    role: string;
    willBeDeleted: boolean;
    newOwner?: {
      id: string;
      displayName: string;
    };
    quests: {
      id: string;
      title: string;
      totalRuns: number;
      willBeDeleted: boolean;
    }[];
  }[];
  totalQuestsToDelete: number;
  totalRunsToDelete: number;
  subscription?: {
    willBeCancelled: boolean;
    cancellationNote: string;
  };
}

const fetchDeletionAnalysis = () =>
  createRequest<DeletionAnalysis>("GET", "/users/me/deletion/analyze")();

const initiateDeletion = () =>
  apiRequest<{ jobId: string; message: string }>(
    "POST",
    "/users/me/deletion",
    {}
  );

export const DeleteAccountModal: React.FC<DeleteAccountModalProps> = ({
  showModal,
  setShowModal,
}) => {
  const [confirmed, setConfirmed] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const snackbarContext = useContext(SnackbarContext);

  const {
    data: analysis,
    error,
    isValidating,
  } = useRequest(showModal ? fetchDeletionAnalysis() : noopRequest());

  const handleDelete = useCallback(async () => {
    if (!confirmed) {
      return;
    }

    setIsDeleting(true);

    try {
      await initiateDeletion();

      snackbarContext.sendMessage(
        "Account deletion initiated. Logging you out...",
        SnackbarSeverity.NOTICE
      );

      setTimeout(() => {
        setShowModal(false);
        setTimeout(() => {
          logOut({
            reason: "User initiated account deletion.",
          });
        }, MODAL_CLOSE_DELAY);
      }, MODAL_CLOSE_DELAY);
    } catch (error) {
      console.error("Failed to initiate deletion:", error);
      snackbarContext.sendMessage(
        "Failed to delete account. Please try again or contact support.",
        SnackbarSeverity.WARNING
      );
      setIsDeleting(false);
    }
  }, [confirmed, snackbarContext, setShowModal]);

  const handleCancel = useCallback(() => {
    setConfirmed(false);
    setShowModal(false);
  }, [setShowModal]);

  return (
    <QKModal
      showModal={showModal}
      setShowModal={setShowModal}
      title="Delete Account"
    >
      <ContentScrollView>
        <ContentContainer>
          {isValidating && !analysis && (
            <LoaderContainer>
              <Loader />
            </LoaderContainer>
          )}

          {error && (
            <ErrorText>
              Failed to load account deletion information. Please try again.
            </ErrorText>
          )}

          {analysis && (
            <>
              <WarningBox>
                <WarningEmoji>⚠️</WarningEmoji>
                <WarningText>
                  Your account and all personal information will be permanently
                  deleted. You will no longer be able to log in.
                </WarningText>
              </WarningBox>

              {analysis.subscription && (
                <SubscriptionWarning>
                  <WarningText>
                    {analysis.subscription.cancellationNote}
                  </WarningText>
                </SubscriptionWarning>
              )}

              <WorkspaceListContainer>
                {analysis.personalWorkspace.quests.length > 0 && (
                  <WorkspaceSection>
                    <WorkspaceName>Your Personal Quests</WorkspaceName>
                    <WorkspaceStatus>Will be deleted</WorkspaceStatus>
                    {analysis.personalWorkspace.quests.map((quest) => (
                      <QuestItem key={quest.id}>
                        <QuestTitle>
                          {quest.title}
                          {quest.totalRuns > 0 && (
                            <RunsBadge>
                              {" "}
                              ({quest.totalRuns} run
                              {quest.totalRuns !== 1 ? "s" : ""})
                            </RunsBadge>
                          )}
                        </QuestTitle>
                        <DeleteLabel>Will be deleted</DeleteLabel>
                      </QuestItem>
                    ))}
                  </WorkspaceSection>
                )}

                {analysis.workspaces.map((workspace) => (
                  <WorkspaceSection key={workspace.id}>
                    <WorkspaceName>{workspace.name}</WorkspaceName>
                    {workspace.willBeDeleted ? (
                      <DeleteStatus>
                        This workspace will be deleted
                      </DeleteStatus>
                    ) : (
                      <LeaveStatus>
                        You will leave this workspace
                        {workspace.newOwner && (
                          <NewOwnerText>
                            {" "}
                            • {workspace.newOwner.displayName} will become the
                            owner
                          </NewOwnerText>
                        )}
                      </LeaveStatus>
                    )}
                    {workspace.quests.length > 0 && (
                      <>
                        {workspace.quests.map((quest) => (
                          <QuestItem key={quest.id}>
                            <QuestTitle>
                              {quest.title}
                              {quest.totalRuns > 0 && (
                                <RunsBadge>
                                  {" "}
                                  ({quest.totalRuns} run
                                  {quest.totalRuns !== 1 ? "s" : ""})
                                </RunsBadge>
                              )}
                            </QuestTitle>
                            {quest.willBeDeleted ? (
                              <DeleteLabel>Will be deleted</DeleteLabel>
                            ) : (
                              <PreserveLabel>Will remain</PreserveLabel>
                            )}
                          </QuestItem>
                        ))}
                      </>
                    )}
                  </WorkspaceSection>
                ))}
              </WorkspaceListContainer>

              <ConfirmationSection>
                <ConfirmationLabel>
                  <ConfirmationText>
                    I understand this will permanently delete my account and I
                    will be logged out
                  </ConfirmationText>
                  <Switch
                    switchOn={confirmed}
                    onSwitch={() => setConfirmed(!confirmed)}
                    variant="danger"
                    readOnly={isDeleting}
                  />
                </ConfirmationLabel>
              </ConfirmationSection>

              <ButtonContainer>
                <Button
                  title="Cancel"
                  onPress={handleCancel}
                  buttonType="secondary"
                  disabled={isDeleting}
                />
                <Button
                  title="Permanently Delete"
                  onPress={handleDelete}
                  buttonType="warning"
                  disabled={!confirmed}
                  loading={isDeleting}
                />
              </ButtonContainer>
            </>
          )}
        </ContentContainer>
      </ContentScrollView>
    </QKModal>
  );
};

const ContentScrollView = styled(ScrollView).attrs({
  contentContainerStyle: { padding: 12 },
})``;

const ContentContainer = styled.View`
  gap: 16px;
`;

const LoaderContainer = styled.View`
  width: 100%;
  align-items: center;
  justify-content: center;
  padding: 40px 0;
`;

const ErrorText = styled(Text)`
  color: ${({ theme }) => theme.warning};
  margin-bottom: 20px;
`;

const WarningBox = styled.View`
  background-color: ${colors.yellow50};
  border: 1px solid ${colors.yellow600};
  border-radius: 8px;
  padding: 16px;
  flex-direction: row;
  gap: 12px;
`;

const WarningEmoji = styled(Text)`
  font-size: 24px;
`;

const WarningText = styled(Text)`
  flex: 1;
  font-size: 14px;
  color: ${({ theme }) => theme.card.text};
  line-height: 20px;
`;

const SubscriptionWarning = styled.View`
  background-color: ${colors.primary50};
  border: 1px solid ${colors.primary600};
  border-radius: 8px;
  padding: 16px;
`;

const WorkspaceListContainer = styled.View``;

const WorkspaceSection = styled.View`
  background-color: ${({ theme }) => theme.card.background};
  border: 1px solid ${({ theme }) => theme.interaction.neutralLight};
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 12px;
  gap: 8px;
`;

const WorkspaceName = styled(Text)`
  font-size: 16px;
  font-weight: 600;
  color: ${({ theme }) => theme.card.text};
`;

const WorkspaceStatus = styled(Text)`
  font-size: 13px;
  color: ${({ theme }) => theme.warning};
  font-weight: 500;
`;

const DeleteStatus = styled(Text)`
  font-size: 13px;
  color: ${({ theme }) => theme.warning};
  font-weight: 500;
`;

const LeaveStatus = styled(Text)`
  font-size: 13px;
  color: ${({ theme }) => theme.interaction.neutralMedium};
  font-weight: 500;
`;

const NewOwnerText = styled(Text)`
  font-size: 13px;
  color: ${({ theme }) => theme.interaction.neutralStrong};
`;

const QuestItem = styled.View`
  background-color: ${({ theme }) => theme.background};
  border: 1px solid ${({ theme }) => theme.interaction.neutralLight};
  border-radius: 8px;
  padding: 12px;
  margin-top: 4px;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
`;

const QuestTitle = styled(Text)`
  font-size: 14px;
  color: ${({ theme }) => theme.card.text};
  flex: 1;
  margin-right: 12px;
`;

const RunsBadge = styled(Text)`
  font-size: 12px;
  color: ${({ theme }) => theme.interaction.neutralMedium};
`;

const DeleteLabel = styled(Text)`
  font-size: 12px;
  color: ${({ theme }) => theme.warning};
  font-weight: 500;
`;

const PreserveLabel = styled(Text)`
  font-size: 12px;
  color: ${colors.green500};
  font-weight: 500;
`;

const ConfirmationSection = styled.View`
  margin-top: 8px;
`;

const ConfirmationLabel = styled.View`
  flex-direction: row;
  align-items: center;
  gap: 12px;
  justify-content: space-between;
`;

const ConfirmationText = styled(Text)`
  flex: 1;
  font-size: 14px;
  color: ${({ theme }) => theme.warning};
  margin-right: 12px;
`;

const ButtonContainer = styled.View`
  flex-direction: row;
  gap: 12px;
  margin-top: 16px;
  justify-content: flex-end;
`;
